/**
 * Customer Waitlist Page — shown pre-launch when PUBLIC_BOOKING_ENABLED=false.
 *
 * Required fields: name, email, phone, vehicle_type. Validation runs
 * client-side via `guardSubmission` BEFORE any network call so blank
 * forms never hit the backend (defence-in-depth: backend re-validates
 * and returns 400 on bad payloads).
 *
 * Phase 7g.15.48 — Twilio A2P CTA proof (Rick directive 2026-05-27):
 *   Adds an UNCHECKED, OPTIONAL SMS consent checkbox under the phone
 *   field with Twilio-safe disclosure language and visible links to
 *   /terms and /privacy.
 */
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Mail, MapPin, ArrowLeft, Loader2, CheckCircle2, User, Phone, Car, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { apiClient } from "@/App";
import { guardSubmission, REQUIRED_CUSTOMER_WAITLIST } from "@/lib/formValidation";

// i18n sweep 2026-06-11 — option VALUES stay EN verbatim (backend
// lead-scoring buckets + admin exports key on these exact strings);
// only the visible LABEL is translated via labelKey.
const VEHICLE_OPTIONS = [
  { value: "Car / SUV", labelKey: "waitlist.veh_car" },
  { value: "Truck", labelKey: "waitlist.veh_truck" },
  { value: "Boat", labelKey: "waitlist.veh_boat" },
  { value: "Watercraft / PWC", labelKey: "waitlist.veh_pwc" },
  { value: "RV / Motorhome", labelKey: "waitlist.veh_rv" },
  { value: "Fleet (commercial)", labelKey: "waitlist.veh_fleet" },
  { value: "Golf Cart", labelKey: "waitlist.veh_golf" },
  { value: "Recreational Vehicle", labelKey: "waitlist.veh_rec" },
  { value: "Luxury / Exotic", labelKey: "waitlist.veh_luxury" },
  { value: "Classic / Collectible", labelKey: "waitlist.veh_classic" },
  // Phase 7g.15.47 — Rick QA 2026-05-25. Aircraft is cosmetic-detailing
  // only and ALWAYS handled as a custom quote. Submissions tagged with
  // this vehicle type get aviation-bucket lead scoring on the backend.
  { value: "Small Aircraft / Private Plane", labelKey: "waitlist.veh_aircraft" },
];

// Twilio A2P-compliant SMS consent disclosure (locked verbatim per Rick directive 2026-05-27, second pass).
// MUST remain visible next to the unchecked checkbox on the customer waitlist form.
const SMS_CONSENT_TEXT = "I agree to receive SMS text messages from WashPros Global Services LLC about my account, waitlist status, bookings, service updates, support requests, and operational notices. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not required to purchase services or join the waitlist.";

const CustomerWaitlistPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [zip, setZip] = useState("");
  const [smsConsent, setSmsConsent] = useState(false); // UNCHECKED by default — optional, never prechecked
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorFields, setErrorFields] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorFields([]);

    const payload = { name, email, phone, vehicle_type: vehicleType, zip_code: zip };
    const guard = guardSubmission(payload, REQUIRED_CUSTOMER_WAITLIST);
    if (!guard.ok) {
      setErrorFields(guard.fields || []);
      toast.error(guard.error);
      return;
    }

    // Attach SMS consent fields (Twilio A2P CTA proof) — sms_consent_text
    // is included ONLY when the user ticked the box, so we never claim
    // consent we didn't have. sms_consent_source identifies the origin
    // page for downstream audit.
    const submitPayload = {
      ...guard.cleaned,
      sms_consent: smsConsent,
      sms_consent_source: "customer_waitlist",
    };
    if (smsConsent) {
      submitPayload.sms_consent_text = SMS_CONSENT_TEXT;
    }

    setLoading(true);
    try {
      await apiClient.post("/waitlist/customer", submitPayload);
      setSubmitted(true);
      toast.success(t("waitlist.success_toast"));
    } catch (err) {
      // Phase 7g.15.99c (2026-06-09) — Disambiguated waitlist error toasts.
      // Previously every non-validation error became "Couldn't reach our
      // servers", which (a) misled users when they actually hit the 5/min
      // rate-limit and (b) hid 5xx incidents behind a network-only message.
      // Order matters: known 4xx contracts first → 429 → 5xx → network last.
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;

      if (detail?.error === "missing_required_fields") {
        setErrorFields(detail.fields || []);
        toast.error(detail.message || t("waitlist.err_blank_fields"));
        return;
      }
      if (detail?.error === "invalid_email") {
        setErrorFields(["email"]);
        toast.error(detail.message || t("waitlist.err_invalid_email"));
        return;
      }
      // 429 — rate-limited. Body is `{error:"rate_limit_exceeded", message, detail}`
      // where `detail` is the rate string ("5 per 1 minute"), NOT an object.
      // Surface a clear retry-after message so users know to wait, not retry instantly.
      if (status === 429 || err?.response?.data?.error === "rate_limit_exceeded") {
        toast.error(t("waitlist.err_rate_limited"));
        return;
      }
      // 5xx — backend incident. Don't blame the network; offer magic-link
      // recovery is not relevant here (this isn't auth). Just acknowledge.
      if (status >= 500 && status < 600) {
        toast.error(t("waitlist.err_server"));
        return;
      }
      // No response object at all means it really was a network/CORS failure.
      if (!err?.response) {
        toast.error(t("waitlist.err_network"));
        return;
      }
      // Any other 4xx — surface the server-provided message when present.
      const fallback = (typeof detail === "string" && detail)
        || detail?.message
        || err?.response?.data?.message
        || t("waitlist.err_generic");
      toast.error(fallback);
    } finally {
      setLoading(false);
    }
  };

  const errClass = (field) => errorFields.includes(field) ? "border-red-500/60 ring-1 ring-red-500/40" : "border-white/10";

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-4 py-12" data-testid="customer-waitlist-page">
      <button
        onClick={() => navigate("/")}
        className="absolute top-6 left-6 text-slate-400 hover:text-white flex items-center gap-2 text-sm"
        data-testid="waitlist-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </button>

      <div className="max-w-xl w-full text-center space-y-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400/10 border border-amber-300/30 text-amber-300 text-xs font-bold uppercase tracking-widest">
          <Sparkles className="w-3.5 h-3.5" /> {t("waitlist.coming_soon")}
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
          The Carwash Concierge<br />{t("waitlist.launching_suffix")}
        </h1>

        <p className="text-slate-400 text-lg max-w-md mx-auto">
          {t("waitlist.subtitle")}
        </p>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-3 pt-4 max-w-md mx-auto" noValidate>
            {/* Phase 7g.15.66 fix #7: bumped pl-12 → pl-14 so the
                left icons never overlap placeholder/typed text on
                narrow vertical phone widths (≤ 360 px). */}
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <Input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t("waitlist.name_ph")}
                className={`pl-14 h-14 bg-slate-900 ${errClass("name")} text-white text-lg`}
                required data-testid="waitlist-name-input"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t("waitlist.email_ph")}
                className={`pl-14 h-14 bg-slate-900 ${errClass("email")} text-white text-lg`}
                required data-testid="waitlist-email-input"
              />
            </div>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <Input
                type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder={t("waitlist.phone_ph")}
                className={`pl-14 h-14 bg-slate-900 ${errClass("phone")} text-white text-lg`}
                required data-testid="waitlist-phone-input"
              />
            </div>

            {/* Phase 7g.15.48 — Twilio A2P CTA compliance.
                UNCHECKED by default · OPTIONAL · never prechecked.
                Consent text + Terms/Privacy links visible inline. */}
            <button
              type="button"
              data-testid="waitlist-sms-consent-checkbox"
              onClick={() => setSmsConsent((v) => !v)}
              aria-pressed={smsConsent}
              aria-label="Optional SMS consent"
              className={`w-full flex items-start gap-3 p-4 rounded-xl border transition-all text-left ${
                smsConsent
                  ? "bg-emerald-500/10 border-emerald-500/30"
                  : "bg-slate-900/60 border-white/10 hover:bg-slate-900/80"
              }`}
            >
              {smsConsent ? (
                <CheckSquare className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              ) : (
                <Square className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              )}
                <span
                  className="text-xs text-slate-300 leading-relaxed"
                  data-testid="waitlist-sms-consent-label"
                >
                  {SMS_CONSENT_TEXT} See our{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-cyan-400 hover:underline"
                    data-testid="waitlist-sms-consent-terms-link"
                  >
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-cyan-400 hover:underline"
                    data-testid="waitlist-sms-consent-privacy-link"
                  >
                    Privacy Policy
                  </Link>
                  . This SMS opt-in is optional — leaving it unchecked will not affect your waitlist position.
                </span>
            </button>

            <div className="relative">
              <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none z-10" />
              <select
                value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}
                className={`pl-12 pr-4 h-14 w-full bg-slate-900 ${errClass("vehicle_type")} rounded-md text-white text-lg appearance-none border`}
                required data-testid="waitlist-vehicle-type-select"
              >
                <option value="" disabled className="text-slate-500">{t("waitlist.vehicle_ph")}</option>
                {VEHICLE_OPTIONS.map((v) => (<option key={v.value} value={v.value}>{t(v.labelKey)}</option>))}
              </select>
            </div>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text" value={zip} onChange={(e) => setZip(e.target.value)}
                placeholder={t("waitlist.zip_ph")}
                className="pl-12 h-14 bg-slate-900 border-white/10 text-white text-lg"
                data-testid="waitlist-zip-input"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full h-14 text-lg font-bold bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
              data-testid="waitlist-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("waitlist.join_cta")}
            </Button>
          </form>
        ) : (
          <div className="pt-4 space-y-4 max-w-md mx-auto" data-testid="waitlist-success">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <p className="text-emerald-300 text-lg font-semibold">{t("waitlist.success_title")}</p>
            <p className="text-slate-400 text-sm">{t("waitlist.success_prefix")} <span className="text-white">{email}</span> {t("waitlist.success_suffix")}</p>
          </div>
        )}

        <div className="pt-12 border-t border-white/5 mt-12">
          <p className="text-slate-500 text-sm mb-3">{t("waitlist.vendor_question")}</p>
          <Button
            variant="outline"
            onClick={() => navigate("/vendor/apply")}
            className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
            data-testid="waitlist-vendor-cta"
          >
            {t("waitlist.vendor_cta")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerWaitlistPage;
